// ---------------------------------------------------------------------------
// The account, as the rest of the app is allowed to see it.
//
// Everything to do with Clerk goes through this one context, for three reasons
// that are not stylistic:
//
//   1. Clerk's hooks throw when there is no <ClerkProvider> above them, and on a
//      deployment with no publishable key there deliberately is none. A screen
//      that called useAuth() directly would work in production and crash on a
//      local run, which is exactly backwards.
//   2. Guest play is a supported mode, not a degraded one. The default value of
//      this context is the guest-only one, so a component rendered outside the
//      provider reports "no accounts here" rather than failing.
//   3. The only personal facts this app may ever show are the username, the
//      display name, and the games (DESIGN.md section 4). So the Clerk user
//      object is never read at all: identity comes from our own /api/me, which
//      returns nothing else. There is no email address or Clerk user id
//      anywhere in this type, and there should never be one.
// ---------------------------------------------------------------------------

import { createContext, useContext } from 'react';
import type { Account } from '../lib/api.js';

export interface AccountApi {
  /** Clerk is configured for this deployment. False means no sign-in affordance may render. */
  enabled: boolean;
  /** Clerk has loaded and, when signed in, /api/me has answered one way or the other. */
  ready: boolean;
  signedIn: boolean;
  /** Null when signed out, and when /api/me could not answer. */
  account: Account | null;
  /** Why the last account call failed, in the server's words. */
  error: string | null;
  signIn: () => void;
  signOut: () => void;
  /** Claim a permanent username. Rejects with the server's reason on 400, 409 or 429. */
  chooseUsername: (username: string, displayName?: string) => Promise<void>;
  /** Adopt this device's guest games. Resolves with how many were adopted. */
  adoptGuestGames: () => Promise<number>;
  /**
   * A session token for a room join, fetched at the moment it is needed. Clerk
   * rotates the session JWT roughly once a minute, so anything held from mount
   * is expired by the time a player uses it. Undefined when signed out.
   */
  freshToken: () => Promise<string | undefined>;
}

const notAvailable = () => Promise.reject(new Error('Signing in is not available.'));

/**
 * What every consumer sees when Clerk is not configured. Not an error state:
 * this is how the site shipped and how a local run always behaves.
 */
export const GUEST_ONLY: AccountApi = {
  enabled: false,
  ready: true,
  signedIn: false,
  account: null,
  error: null,
  signIn: () => {},
  signOut: () => {},
  chooseUsername: notAvailable,
  adoptGuestGames: () => Promise.resolve(0),
  freshToken: () => Promise.resolve(undefined),
};

/**
 * Before /api/config has answered, whether this deployment has accounts at all
 * is unknown. It reads as a guest — no sign-in affordance may render on a maybe
 * — but `ready` is false, so a room join waits rather than committing to one.
 */
export const ACCOUNT_UNKNOWN: AccountApi = { ...GUEST_ONLY, ready: false };

export const AccountContext = createContext<AccountApi>(GUEST_ONLY);

export function useAccount(): AccountApi {
  return useContext(AccountContext);
}
